import React, { useEffect, useState, useRef } from "react";
import { OFFICE_VERTICALS, SERVICE_VERTICALS, NO_POS, NO_WHOLESALE, NO_PURCHASES, NO_INVENTORY, NO_DELIVERIES, NAV_GROUPS, NAV, GROUP_META, DEFAULT_META, cleanGroupTitle, GROUP_TITLE_KEY, ROLE_LABELS } from "./layout/navigationConfig";
import { Outlet, NavLink, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, ShoppingCart, Package, Truck, FileText, Users, Building2, DollarSign,
  BarChart3, LogOut, Menu, X, User as UserIcon, ScanBarcode, UserCog, Landmark, Sparkles, BookOpen, Wallet, Settings as SettingsIcon, Inbox, MessageCircle, Receipt, TrendingDown, CreditCard, Users2, BookOpenCheck, Mail, ChevronDown, RotateCcw, Trophy, Banknote, ShieldAlert, Warehouse, ClipboardList, Repeat, Database, ScrollText, Activity, KeyRound, RefreshCw
  } from "lucide-react";
import { LucideIcon } from "@/components/LucideIcon";
import InstallPWA from "@/components/InstallPWA";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import { LockButton, LockGuard } from "@/components/ScreenLock";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import OfflineStatus from "@/components/OfflineStatus";
import OnboardingTour from "@/components/OnboardingTour";
import { useFeature } from "@/contexts/FeaturesContext";
import { useI18n } from "@/contexts/I18nContext";
import { enableWebPush } from "@/lib/push";
import QuickActions from "@/components/QuickActions";
import { Briefcase, Layers, ClipboardCheck, FileText as FileTextIcon, UserPlus, Clock, LifeBuoy, Gift, Utensils, ChefHat, QrCode, Stethoscope, CalendarCheck, HeartPulse, Ticket, Factory, ShieldCheck, Pill, Crown, ChevronRight, Share2, ArrowLeftRight, Send, FileSignature, FolderKanban } from "lucide-react";

export default function Layout() {
  const { user, companies, companyId, setCompanyId, currentCompany, logout } = useAuth();
  const { t } = useI18n();
  const gT = (title) => { const k = GROUP_TITLE_KEY[cleanGroupTitle(title)]; return k ? t(k) : cleanGroupTitle(title); };
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [customItems, setCustomItems] = useState([]);
  const [noteItem, setNoteItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dailyRate, setDailyRate] = useState(null);
  const [sypAmount, setSypAmount] = useState(1000000);
  const [rateLoading, setRateLoading] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const prevUnreadRef = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // Play a short notification beep using the Web Audio API (no asset needed)
  const playChatBeep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const notes = [880, 1174.66]; // A5 -> D6 (pleasant two-tone ding)
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const t = now + i * 0.15;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.3);
      });
      setTimeout(() => { try { ctx.close(); } catch {} }, 900);
    } catch {}
  };

  const showChatNotification = (count) => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const n = new Notification("محاسبتي — رسالة جديدة", {
          body: count > 1 ? `لديك ${count} رسائل غير مقروءة في الدردشة` : "وصلتك رسالة جديدة في الدردشة",
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag: "chat-message",
          renotify: true,
        });
        n.onclick = () => { try { window.focus(); nav("/chat"); n.close(); } catch {} };
      }
    } catch {}
  };

  useEffect(() => {
    let alive = true;
    // Ask for OS notification permission once, then subscribe this device to Web Push
    try {
      if (typeof Notification !== "undefined") {
        if (Notification.permission === "granted") {
          enableWebPush();
        } else if (Notification.permission === "default") {
          Notification.requestPermission().then((p) => { if (p === "granted") enableWebPush(); }).catch(() => {});
        }
      }
    } catch {}
    const poll = async () => {
      try {
        const { data } = await api.get("/chat/unread-count");
        if (!alive) return;
        const n = data?.unread || 0;
        const prev = prevUnreadRef.current;
        // Alert only when count increases (a new message arrived), skipping the first baseline poll
        if (prev !== null && n > prev) {
          const activelyReading = !document.hidden && pathRef.current === "/chat";
          if (!activelyReading) {
            playChatBeep();
            showChatNotification(n);
          }
        }
        prevUnreadRef.current = n;
        setChatUnread(n);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 15000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    window.addEventListener("chat-read", poll);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); window.removeEventListener("chat-read", poll); };
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/system/menu-items"); setCustomItems(data); } catch {}
    })();
  }, []);

  const loadDailyRate = async () => {
    setRateLoading(true);
    try {
      const { data } = await api.get("/currencies");
      const base = data.base || currentCompany?.base_currency || "SYP";
      const rates = data.rates || {};
      const other = base === "USD" ? "SYP" : "USD";
      const raw = Number(rates[other]);
      setDailyRate({ base, other, value: raw > 0 ? (1 / raw) : null, updatedAt: data.updated_at });
    } catch { setDailyRate(null); }
    finally { setRateLoading(false); }
  };
  useEffect(() => { if (currentCompany?.id) loadDailyRate(); }, [currentCompany?.id]); // eslint-disable-line

  const openCustom = (it) => {
    if (it.url) {
      window.open(it.url, "_blank", "noopener");
    } else {
      setNoteItem(it);
    }
    setOpen(false);
  };

  const canSee = (item) => !item.roles || user.role === "super_admin" || item.roles.includes(user.role);
  const aiChatOn = useFeature("ai_chat");
  const multiCurOn = useFeature("multi_currency");
  const cashierOn = useFeature("cashier_pos");
  const reportsOn = useFeature("reports");
  const warehousesOn = useFeature("warehouses");
  const featureMap = { ai_chat: aiChatOn, multi_currency: multiCurOn, cashier_pos: cashierOn, reports: reportsOn, warehouses: warehousesOn };
  const hasFeature = (item) => !item.feature || featureMap[item.feature] !== false;
  const subscriptionPlan = currentCompany?.subscription_plan || (currentCompany?.public_store_enabled ? "accounting_ecommerce" : "accounting");
  const ecommerceOnly = subscriptionPlan === "ecommerce" && !user?.is_root;
  const ecommerceNavKeys = new Set(["guide", "users", "companies", "company_feature_restrictions", "company_feature_assignments", "online_store", "store_approvals", "custom_roles", "two_fa", "support_access", "support_activity", "tickets"]);
  const hasProductAccess = (item) => {
    if (ecommerceOnly) return ecommerceNavKeys.has(item.key);
    if (subscriptionPlan === "accounting" && !user?.is_root) return item.key !== "online_store";
    return true;
  };
  const ecommercePaths = ["/guide", "/online-store", "/users", "/custom-roles", "/2fa", "/support/access", "/support/activity", "/tickets", "/companies", "/plans", "/subscriptions", "/store-approvals"];
  const ecommercePathAllowed = ecommercePaths.some(path => location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`)));
  // Business-type vertical gating: items/groups tagged with `verticals` appear ONLY for a
  // company whose business_type matches. Specialized verticals (restaurant/factory/pharmacy/
  // clinic) are fully independent — a general/other account never sees them.
  const bType = currentCompany?.business_type || "general_trading";
  const previewAllFeatures = process.env.REACT_APP_MOCK_MODE === "true" && user?.is_root;
  const matchesVertical = (item) => {
    if (previewAllFeatures) return true;
    // Hide if item is explicitly hidden for this vertical
    if (item.hidden_for && item.hidden_for.includes(bType)) return false;
    // Show if item has no vertical restriction, or if this exact vertical is allowed
    return !item.verticals || item.verticals.includes(bType);
  };
  const visibleNav = NAV.filter(i => canSee(i) && hasFeature(i) && hasProductAccess(i) && matchesVertical(i));

  const [homeGroup, setHomeGroup] = React.useState(null);

  // Collapsed groups persistence in localStorage: comma-separated group titles that are COLLAPSED
  const [collapsedGroups, setCollapsedGroups] = React.useState(() => {
    try { return new Set((localStorage.getItem("nav_collapsed") || "").split("|").filter(Boolean)); }
    catch { return new Set(); }
  });
  const toggleGroup = (title) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      localStorage.setItem("nav_collapsed", Array.from(next).join("|"));
      return next;
    });
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const renderSidebar = (mobile = false) => (
    <aside className={`${mobile ? "fixed inset-y-0 end-0 z-50 w-[min(300px,86vw)] shadow-2xl items-stretch px-3" : "hidden lg:flex w-[92px] shrink-0 sticky top-0 h-screen items-center"} bg-card border-s border-border flex-col py-4 overflow-y-auto`} dir="rtl">
      <Link to="/" onClick={() => mobile && setSidebarOpen(false)} className={`${mobile ? "w-full h-14 justify-start gap-3 px-2" : "w-14 h-14 justify-center"} rounded-2xl bg-white grid grid-flow-col auto-cols-max place-items-center shadow-sm mb-5 border border-border overflow-hidden`} aria-label="الرئيسية">
        <img src="/brand/logo-mark.png" alt="محاسبتي" className="w-12 h-12 object-contain p-1" />
        {mobile && <span className="font-heading font-bold text-base text-slate-800">محاسبتي</span>}
      </Link>
      <nav className={`w-full ${mobile ? "px-0 space-y-3" : "px-2 space-y-4"}`} aria-label="التنقل الرئيسي">
        {NAV_GROUPS.map(group => {
          const visible = group.items.filter(i => canSee(i) && hasFeature(i) && hasProductAccess(i) && matchesVertical(i));
          if (!visible.length) return null;
          if (group.verticals && !previewAllFeatures && !group.verticals.includes(bType)) return null;
          if (group.verticals_hidden && group.verticals_hidden.includes(bType)) return null;
          return (
            <div key={group.title} className="space-y-1.5">
              <div className={`${mobile ? "text-xs text-start px-2" : "text-[9px] text-center px-1"} font-bold text-muted-foreground truncate`} title={gT(group.title)}>{gT(group.title)}</div>
              {visible.map(({to, key, icon: Icon, end}) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => mobile && setSidebarOpen(false)}
                  title={t(key)}
                  aria-label={t(key)}
                  className={({ isActive }) => `group relative ${mobile ? "w-full min-h-12 h-auto px-3 py-2 flex-row justify-start gap-3 rounded-xl" : "mx-auto w-16 h-16 flex-col justify-center gap-1 rounded-2xl"} border flex items-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isActive ? "bg-primary text-primary-foreground border-primary shadow-md" : "bg-background border-border text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-primary/5 hover:-translate-y-0.5"}`}
                >
                  <Icon className="w-5 h-5" />
                  {to === "/chat" && chatUnread > 0 && (
                    <span className="absolute top-1 end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-card animate-pulse" data-testid="chat-unread-badge">{chatUnread > 99 ? "99+" : chatUnread}</span>
                  )}
                  <span className={`${mobile ? "max-w-full text-sm" : "max-w-[58px] text-[9px]"} truncate font-semibold leading-tight`}>{t(key)}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );

  if (ecommerceOnly && !ecommercePathAllowed) return <Navigate to="/online-store" replace />;
  if (subscriptionPlan === "accounting" && !user?.is_root && location.pathname.startsWith("/online-store")) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen w-full max-w-[100vw] bg-background flex overflow-x-clip">
      {renderSidebar()}
      {sidebarOpen && <><div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />{renderSidebar(true)}</>}
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="min-h-16 h-auto bg-card border-b border-border flex flex-wrap items-center justify-between gap-y-2 px-2 sm:px-4 md:px-6 py-2 lg:py-0 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" aria-label="الرئيسية">
              <div className="w-9 h-9 rounded-md bg-white border border-border grid place-items-center overflow-hidden"><img src="/brand/logo-mark.png" alt="محاسبتي" className="w-full h-full object-contain p-0.5" /></div>
              <div className="hidden sm:block"><div className="font-heading font-bold text-base leading-tight">محاسبتي</div><div className="text-[10px] text-muted-foreground">MultiCo Accounting</div></div>
            </Link>
            <div className="hidden md:block border-s border-border ps-3">
              <div className="text-xs text-muted-foreground">الشركة النشطة</div>
              <div className="font-heading font-bold text-sm">{currentCompany?.name || "لم يتم اختيار شركة"}</div>
            </div>
          </div>
          <div className="hidden sm:flex flex-1 max-w-md mx-2 md:mx-4 min-w-0">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <Button variant="outline" size="icon" className="lg:hidden h-10 w-10" onClick={() => setSidebarOpen(true)} aria-label="فتح القائمة"><Menu className="w-4 h-4" /></Button>
            <span className="hidden sm:block"><OfflineStatus /></span>
            <Button variant="outline" size="sm" onClick={loadDailyRate} disabled={rateLoading} className="hidden xl:inline-flex h-9 gap-1.5 text-xs" title="تحديث سعر الصرف يدويًا" data-testid="daily-rate-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${rateLoading ? "animate-spin" : ""}`} />
              {dailyRate?.value ? `1 ${dailyRate.base} = ${dailyRate.value.toLocaleString("en-US", { maximumFractionDigits: 4 })} ${dailyRate.other}` : "سعر الصرف"}
            </Button>
            {dailyRate?.value && <div className="hidden 2xl:flex items-center gap-1 text-xs border rounded-md px-2 h-9 bg-muted/20" title="حاسبة الليرة إلى الدولار">
              <Input type="number" min="0" value={sypAmount} onChange={e => setSypAmount(e.target.value)} className="w-24 h-7 px-1 text-xs text-end" aria-label="مبلغ بالليرة السورية" />
              <span>SYP ≈</span><b>{(Number(sypAmount || 0) * (dailyRate.base === "SYP" ? dailyRate.value : 1 / dailyRate.value)).toLocaleString("en-US", {maximumFractionDigits: 2})}</b><span>USD</span>
            </div>}
            <ThemeToggle />
            <LanguageSwitcher />
            <span className="hidden md:block"><InstallPWA /></span>
            <span className="hidden sm:block"><NotificationsBell /></span>
            <span className="hidden md:block"><LockButton /></span>
            {companies.length > 0 && (
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger className="w-24 sm:w-52 h-9 text-xs sm:text-sm" data-testid="company-selector">
                  <SelectValue placeholder="اختر شركة" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.base_currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 h-9" data-testid="user-menu-button">
                  <UserIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>
                  <div>{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                  <div className="text-xs mt-1 inline-block px-2 py-0.5 rounded bg-primary/10 text-primary">{ROLE_LABELS[user?.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4 me-2" /> تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {process.env.REACT_APP_MOCK_MODE === "true" && <div className="mx-2 mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 sm:mx-4 md:mx-6" role="status" data-testid="demo-mode-banner">بيئة تجريبية — يمكنك تجربة الشاشات والعمليات؛ البيانات محفوظة محليًا في هذا المتصفح فقط ولا تمثل بيانات حقيقية.</div>}
        <main className="flex-1 min-w-0 p-2 sm:p-4 md:p-6 overflow-auto">
          {location.pathname === "/" && (() => {
            const groups = NAV_GROUPS.map(group => {
              const visible = group.items.filter(i => canSee(i) && hasFeature(i) && matchesVertical(i));
              if (group.verticals && !previewAllFeatures && !group.verticals.includes(bType)) return null;
              if (group.verticals_hidden && group.verticals_hidden.includes(bType)) return null;
              if (!visible.length) return null;
              return { ...group, visible };
            }).filter(Boolean);
            const active = homeGroup === "__custom__"
              ? { title: "التواصل والروابط الخارجية", custom: true }
              : (groups.find(g => g.title === homeGroup) || null);
            const activeMeta = active ? (GROUP_META[active.title] || DEFAULT_META) : null;
            const ActiveIcon = activeMeta?.icon || LayoutDashboard;
            return (
              <section className="mb-8" data-testid="home-navigation-tiles">
                {!active ? (
                  <>
                    <div className="mb-6"><h1 className="text-2xl md:text-3xl font-heading font-bold">{t("choose_section")}</h1><p className="text-sm text-muted-foreground mt-1">{t("choose_section_desc")}</p></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {groups.map(g => {
                        const meta = GROUP_META[g.title] || DEFAULT_META;
                        const GIcon = meta.icon;
                        return (
                          <button key={g.title} data-testid={`home-group-${cleanGroupTitle(g.title).replaceAll(" ", "-")}`} onClick={() => setHomeGroup(g.title)}
                            className="group relative overflow-hidden min-h-36 rounded-3xl p-5 flex flex-col justify-between text-start shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)`, color: "#fff" }}>
                            <span className="w-12 h-12 rounded-2xl grid place-items-center backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.22)" }}><GIcon className="w-6 h-6" /></span>
                            <div className="relative z-10">
                              <div className="font-heading font-bold text-lg leading-tight">{gT(g.title)}</div>
                              <div className="text-xs opacity-90 mt-0.5">{g.visible.length} {t("tool_word")}</div>
                            </div>
                            <GIcon className="absolute -left-6 -bottom-6 w-28 h-28 opacity-10 pointer-events-none" />
                          </button>
                        );
                      })}
                      <button data-testid="home-group-custom" onClick={() => setHomeGroup("__custom__")}
                        className="group relative overflow-hidden min-h-36 rounded-3xl p-5 flex flex-col justify-between text-start shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2"
                        style={{ background: "linear-gradient(135deg, #0284c7, #0284c7cc)", color: "#fff" }}>
                        <span className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: "rgba(255,255,255,0.22)" }}><Share2 className="w-6 h-6" /></span>
                        <div className="relative z-10"><div className="font-heading font-bold text-lg leading-tight">{t("grp_comm")}</div><div className="text-xs opacity-90 mt-0.5">{customItems.length} {t("link_word")}</div></div>
                        <Share2 className="absolute -left-6 -bottom-6 w-28 h-28 opacity-10 pointer-events-none" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-6 flex items-center gap-3">
                      <button onClick={() => setHomeGroup(null)} data-testid="home-group-back" className="h-10 px-3 rounded-xl border border-border bg-card flex items-center gap-1.5 text-sm font-semibold hover:bg-muted transition-colors"><ChevronRight className="w-4 h-4" /> {t("back")}</button>
                      <span className="w-11 h-11 rounded-2xl grid place-items-center text-white shadow-sm" style={{ background: activeMeta.color }}><ActiveIcon className="w-6 h-6" /></span>
                      <h1 className="text-2xl font-heading font-bold">{active.custom ? t("grp_comm") : gT(active.title)}</h1>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                      {active.custom
                        ? (customItems.length > 0
                            ? customItems.map(it => (
                            <button key={it.id} onClick={() => openCustom(it)} data-testid={`custom-tile-${it.id}`} className="group min-h-28 rounded-2xl border border-border bg-card p-4 flex flex-col items-center justify-center gap-3 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-md" style={{ ["--gc"]: activeMeta.color }}>
                              <span className="w-12 h-12 rounded-xl grid place-items-center transition-colors" style={{ background: `${activeMeta.color}1a`, color: activeMeta.color }}><LucideIcon name={it.icon} /></span>
                              <span className="text-sm font-semibold leading-tight">{it.label}</span>
                            </button>
                          ))
                            : (
                              <div className="col-span-full text-center py-14 border-2 border-dashed rounded-2xl text-muted-foreground" data-testid="custom-empty-state">
                                <Share2 className="w-12 h-12 mx-auto opacity-30 mb-3" />
                                <div className="font-semibold mb-1 text-foreground">لا توجد روابط تواصل بعد</div>
                                <div className="text-sm mb-4">أضِف روابط التواصل الخارجية (واتساب، فيسبوك، الموقع، ...) وستظهر هنا وفي الشريط الجانبي.</div>
                                {user?.role === "super_admin" && <Link to="/system" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid="custom-add-link">الذهاب للإعدادات لإضافة رابط ←</Link>}
                              </div>
                            ))
                        : active.visible.map(({ to, key, icon: Icon, end }) => (
                            <NavLink key={to} to={to} end={end} data-testid={`home-tile-${to.replaceAll("/", "-").replace(/^-/, "")}`} className="group relative min-h-28 rounded-2xl border border-border bg-card p-4 flex flex-col items-center justify-center gap-3 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
                              style={{ ["--gc"]: activeMeta.color }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = activeMeta.color; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = ""; }}>
                              {to === "/chat" && chatUnread > 0 && (
                                <span className="absolute top-2 end-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shadow animate-pulse" data-testid="chat-unread-badge-tile">{chatUnread > 99 ? "99+" : chatUnread}</span>
                              )}
                              <span className="w-12 h-12 rounded-xl grid place-items-center transition-colors" style={{ background: `${activeMeta.color}1a`, color: activeMeta.color }}><Icon className="w-6 h-6" /></span>
                              <span className="text-sm font-semibold leading-tight">{t(key)}</span>
                            </NavLink>
                          ))}
                    </div>
                  </>
                )}
              </section>
            );
          })()}
          <Outlet />
        </main>
      </div>
      <OnboardingTour />
      <LockGuard />
      <QuickActions />

      <Dialog open={!!noteItem} onOpenChange={(o)=>!o && setNoteItem(null)}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LucideIcon name={noteItem?.icon} className="w-5 h-5 text-primary" />
              {noteItem?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {noteItem?.note || "—"}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
